import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
  Dialog,
  DialogContent,
  IconButton,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CloseIcon from '@material-ui/icons/Close';
import TrackChangesIcon from '@material-ui/icons/TrackChanges';
import { PocBox } from '../utils/PocBox';
import StepStatusTracking from './StepStatusTracking';

const POWERAPPS_URL =
  'https://apps.powerapps.com/play/e/default-ceb177bf-013b-49ab-8a9c-4abce32afc1e/a/ad8c64f4-50a8-4e43-81f9-e72c814733c5?tenantId=ceb177bf-013b-49ab-8a9c-4abce32afc1e&hint=6cbed907-46dd-45a5-b01f-fce04c1786de&sourcetime=1714413283243';

export default function StepRequestForm({ classes }: { classes: any }) {
  const [trackOpen, setTrackOpen] = useState(false);

  return (
    <>
      <Typography variant="body2" style={{ color: '#757575', marginBottom: 20 }}>
        Submit your formal onboarding request using the PowerApps form below. Complete all required fields to avoid delays.
      </Typography>

      

      <Dialog
        open={trackOpen}
        onClose={() => setTrackOpen(false)}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 24px',
          borderBottom: '1px solid #EEE',
        }}>
          <Typography style={{ fontWeight: 700, fontSize: 18, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <TrackChangesIcon style={{ color: '#FFCD11' }} />
            My Submitted Requests
          </Typography>
          <IconButton size="small" onClick={() => setTrackOpen(false)} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </div>
        <DialogContent dividers style={{ padding: 24 }}>
          {trackOpen && <StepStatusTracking classes={classes} />}
        </DialogContent>
      </Dialog>


      <Accordion className={classes.subAccordion} >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography className={classes.subAccordionSummary}>Required Fields — What to Enter</Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.subAccordionDetails} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
          {[
            ['Team Name', 'Your team or squad name (e.g., "Avengers").'],
            ['Team Description', 'Brief description of your team and what it owns and does.'],
            ['App Name ', 'The registered application name from the Entitlements portal.'],
            ['App Description', 'What the application does and its primary purpose.'],
            ['Client ID ', 'The client ID generated in Step 1 (AC, CC, or Pseudo user). Use the + button to add multiple.'],
            ['LeanIX ID', 'Your applications LeanIX ID from LeanIX repository.'],
            ['Business Owner', 'Search and select the business owner responsible for this application.'],
            ['Technical Owner ', 'Search and select the technical owner / developer lead for this integration.'],
            ['CWS Group', 'The CWS group associated with your team (if applicable).'],
            ['Business Case', 'Paste your detailed business case text or a link to the documents from Step 3.'],
            ['External Data Visibility ', 'Select yes or no.'],
            ['API Product ', 'Select only one APIGEE product names (e.g., "Helios Equipment").'],
            ['APIs', 'List the specific endpoint paths being accessed; don\'t select all endpoints in a product (e.g., catDigital/assets/v1).'],
            ['Environments ', 'Select QA, PROD. For PROD access, add call volume and consistency of calls.'],
          ].map(([field, desc]) => (
            <div key={field} className={classes.infoRow}>
              <div className={classes.infoBullet} />
              <Typography className={classes.infoText}>
                <strong>{field}:</strong> {desc}
              </Typography>
            </div>
          ))}
        </AccordionDetails>
      </Accordion>

      {/* Embedded PowerApps form */}
      <div style={{ marginTop: 24, marginBottom: 8 }}>
        <iframe
          title="Onboarding Request Form"
          src={POWERAPPS_URL}
          style={{
            width: '100%',
            height: 680,
            border: 'none',
            borderRadius: 8,
            display: 'block',
          }}
          allow="geolocation; microphone; camera"
          sandbox="allow-storage-access-by-user-activation allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms"
        />
      </div>

      <Typography variant="caption" style={{ color: '#9E9E9E', display: 'block', marginBottom: 16 }}>
        If the form does not load, your browser may be blocking third-party cookies or the PowerApps domain.{' '}
        <a href={POWERAPPS_URL} target="_blank" rel="noopener noreferrer" style={{ color: '#FFCD11' }}>
          Open in a new tab →
        </a>
      </Typography>

      {/* Track submitted requests */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '14px 16px',
        marginBottom: 20,
        borderRadius: 10,
        background: '#F0F4FF',
        border: '1px solid #D6E0FF',
      }}>
        <Typography variant="body2" style={{ color: '#3D4B66', fontWeight: 500 }}>
          Already submitted a request? Track its status on the Azure DevOps board.
        </Typography>
        <Button
          onClick={() => setTrackOpen(true)}
          startIcon={<TrackChangesIcon />}
          className={classes.stepNavBtn}
          style={{ flexShrink: 0 }}
        >
          View My Submitted Requests
        </Button>
      </div>

      <PocBox
        classes={classes}
        contact="APIGEE Team"
        email="api_management@cat.com"
        issues={['Form submission error', 'Missing required field warning', 'Business case link not accepted', 'Duplicate submission']}
      />
    </>
  );
}